import { z } from 'zod';
import { cursorPageQuerySchema } from './pagination.js';
import { EXPERIENCE_LEVELS, SEXES } from './user-detail.js';
import { DIET_TYPES, GOAL_TYPES } from './users.js';
/**
 * Wait tips: the rotating copy the app shows while a plan generates.
 *
 * They used to be compiled into the Flutter binary, so a typo fix cost an App
 * Store release. The console owns them now, and the app reads them from
 * `GET /content/wait-tips` and caches them.
 */
/**
 * The client's own stroke-icon keys — camelCase, unlike every other enum in
 * this contract, because they are not this API's names. They identify icons in
 * the Flutter client's icon set and are served back to it verbatim.
 *
 * Roughly sixty more exist in the client; these are the nine in use. The
 * console renders a picker over this list rather than a free-text field, and
 * the client falls back to a default sparkle on anything it does not know.
 */
export const WAIT_TIP_ICONS = [
    'utensils',
    'camera',
    'dumbbell',
    'messageCircle',
    'cloud',
    'droplet',
    'calendar',
    'trendingUp',
    'swap',
];
/** Three lines at the width the client renders; a fourth pushes the layout. */
export const WAIT_TIP_TEXT_MAX = 110;
export const WAIT_TIP_SLUG_MAX = 60;
/**
 * Words a tip may not use, rejected by the API and repeated here so the form
 * can say so before the operator hits save.
 *
 * The screen exists in its current form because it once congratulated users on
 * a finished plan four seconds in, on a timer, while generation was still
 * running and could still fail — they landed on a home screen that said they
 * had no plan. Only the plan's own status says a plan is ready.
 */
export const WAIT_TIP_FORBIDDEN_COPY = /\b(ready|done|finished|complete)\b/i;
/**
 * Who a tip is for. An empty array is "everyone"; the four are conjunctive.
 *
 * The app applies these, not the API — it caches one payload and serves every
 * user of the device from it.
 */
export const adminWaitTipAudienceSchema = z.object({
    dietType: z.array(z.enum(DIET_TYPES)),
    goal: z.array(z.enum(GOAL_TYPES)),
    sex: z.array(z.enum(SEXES)),
    experienceLevel: z.array(z.enum(EXPERIENCE_LEVELS)),
});
export const adminWaitTipSchema = z.object({
    id: z.string().uuid(),
    /** What the app keys on. Stable across copy edits, unique within a locale. */
    slug: z.string().min(1).max(WAIT_TIP_SLUG_MAX),
    locale: z.string().min(2).max(10),
    iconKey: z.enum(WAIT_TIP_ICONS),
    text: z.string().min(1).max(WAIT_TIP_TEXT_MAX),
    weight: z.number().int().positive(),
    isActive: z.boolean(),
    audience: adminWaitTipAudienceSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
});
/**
 * Slugs are lower-kebab so they read the same in the console, in the app's
 * cache and in a bug report. Not derived from the copy: the copy is meant to
 * be rewritten, and a slug that moved with it would orphan the app's state.
 */
const slugSchema = z
    .string()
    .trim()
    .min(1)
    .max(WAIT_TIP_SLUG_MAX)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lower-case words separated by hyphens, e.g. swap-meal.');
const textSchema = z
    .string()
    .trim()
    .min(1)
    .max(WAIT_TIP_TEXT_MAX)
    .refine((value) => !WAIT_TIP_FORBIDDEN_COPY.test(value), {
    message: 'A tip cannot say the plan is ready, done, finished or complete — only the server can.',
});
export const adminWaitTipCreateRequestSchema = z.object({
    slug: slugSchema,
    locale: z.string().trim().min(2).max(10).default('en-IN'),
    iconKey: z.enum(WAIT_TIP_ICONS),
    text: textSchema,
    weight: z.number().int().positive().max(100).default(10),
    isActive: z.boolean().default(true),
    audience: adminWaitTipAudienceSchema,
});
/**
 * A full replace of the mutable fields. `slug` and `locale` are not among them
 * — those two are the identity the app caches against, and editing one would
 * read as a delete plus a create to every client that already has the old one.
 */
export const adminWaitTipUpdateRequestSchema = z.object({
    iconKey: z.enum(WAIT_TIP_ICONS),
    text: textSchema,
    weight: z.number().int().positive().max(100),
    isActive: z.boolean(),
    audience: adminWaitTipAudienceSchema,
});
export const adminWaitTipParamsSchema = z.object({ id: z.string().uuid() });
export const adminWaitTipListQuerySchema = cursorPageQuerySchema.extend({
    locale: z.string().trim().min(2).max(10).optional(),
    isActive: z.coerce.boolean().optional(),
});
//# sourceMappingURL=wait-tips.js.map